/**
 * What a machine allows from the console and the phone app: its opt-in scopes (the screen, terminals, a node's
 * console), read and set through `…/relay/v1/devices/:id/scopes`. Off until its owner or an administrator turns
 * them on; widening to a scope that types or clicks needs a recent sign-in, asked here once. The machine's own
 * person still decides on the machine (sharing the screen, opening a terminal to remote control).
 */

import { useState } from 'react';

import { ApiError, routes, SIGN_IN_AGAIN } from '../api/client.ts';
import { useAuth } from '../auth/AuthProvider.tsx';
import { useLoad } from '../hooks.ts';
import { ErrorNote, ListBlock, ListRow, Toggle } from './ui.tsx';

export function useMachineScopes(deviceId: string) {
  const held = useLoad(() => routes.deviceScopes(deviceId), [deviceId]);
  const { signInAgain } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const scopes = held.data?.scopes ?? [];
  const available = held.data?.available ?? [];

  /** Replace the opt-in scopes named in `touch` with those in `on`; everything else the machine holds stays. */
  async function change(touch: string[], on: string[]): Promise<boolean> {
    setBusy(true);
    setError(undefined);
    const next = [...scopes.filter((s) => !touch.includes(s)), ...on];
    const put = () => routes.setDeviceScopes(deviceId, next);
    try {
      try {
        await put();
      } catch (e) {
        if (!(e instanceof ApiError && e.code === SIGN_IN_AGAIN) || !signInAgain) throw e;
        const message = typeof (e.body as { message?: unknown })?.message === 'string' ? (e.body as { message: string }).message : 'Sign in again to allow this.';
        await signInAgain(message);
        await put();
      }
      held.reload();
      return true;
    } catch (e) {
      setError(e instanceof ApiError && (e.status === 403 || e.status === 404) ? 'Only its owner or an administrator can change this.' : (e as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  }

  return { scopes, available, known: Boolean(held.data), hidden: Boolean(held.error), busy, error, change, has: (s: string) => scopes.includes(s), reload: held.reload };
}

export type MachineScopes = ReturnType<typeof useMachineScopes>;

function Row({ title, body, checked, onChange, isDisabled }: { title: string; body: string; checked: boolean; onChange: (v: boolean) => void; isDisabled: boolean }) {
  return <ListRow title={title} description={body} control={<Toggle checked={checked} onChange={onChange} label={title} isDisabled={isDisabled} />} />;
}

/** A pair of opt-in scopes, watch and act: acting without watching means nothing, so each follows the other. */
function Pair({ s, read, write, titles, bodies }: { s: MachineScopes; read: string; write: string; titles: [string, string]; bodies: [string, string] }) {
  const r = s.has(read);
  const w = s.has(write);
  const disabled = s.busy || !s.known;
  return (
    <>
      <Row title={titles[0]} body={bodies[0]} checked={r} isDisabled={disabled} onChange={(v) => void s.change([read, write], v ? (w ? [read, write] : [read]) : [])} />
      <Row title={titles[1]} body={bodies[1]} checked={w} isDisabled={disabled} onChange={(v) => void s.change([read, write], v ? [read, write] : r ? [read] : [])} />
    </>
  );
}

/** The rows, calm: no artwork, one toggle each, the step-up said in the words. ListRows for a ListGroup to hold. */
export function MachineAllows({ s, kind, screen }: { s: MachineScopes; kind: 'workbench' | 'node'; screen: boolean }) {
  if (s.hidden) return <ListBlock className="text-sm text-muted">Only this machine's owner or an administrator sees what it allows.</ListBlock>;
  const shell = s.available.includes('shell:read');
  return (
    <>
      {kind === 'workbench' && screen ? (
        <Pair
          s={s}
          read="ui:view"
          write="ui:control"
          titles={['Screen viewing', 'Screen control']}
          bodies={[
            'Watch its window live. Its person still turns sharing on in AI Workbench, and it shows a bar while anyone watches.',
            'Click and type in its window. Each time needs a recent sign-in, and its person must allow clicking and typing in the app.',
          ]}
        />
      ) : null}
      {kind === 'workbench' ? (
        <Pair
          s={s}
          read="terminals:read"
          write="terminals:write"
          titles={['Watching terminals', 'Typing in terminals']}
          bodies={[
            'See the terminals its person opens to remote control in AI Workbench.',
            'Type in those terminals. A terminal is a shell on the machine, so this needs a recent sign-in each time.',
          ]}
        />
      ) : null}
      {kind === 'node' && shell ? (
        <Pair
          s={s}
          read="shell:read"
          write="shell:write"
          titles={['Watching the console', 'Typing in the console']}
          bodies={[
            'See a shell on this node, opened only when someone asks.',
            'Type in that shell. It can change anything on the node, so this needs a recent sign-in each time.',
          ]}
        />
      ) : null}
      {kind === 'node' && !shell ? <ListBlock className="text-sm text-muted">A node has no screen. Its console can be allowed here once the platform offers it.</ListBlock> : null}
      {s.error ? (
        <ListBlock>
          <ErrorNote error={s.error} />
        </ListBlock>
      ) : null}
    </>
  );
}
